import React, { useState } from "react";
import { LucideIcon, Search } from "lucide-react";
import { useWarehouseCatalog, useWarehouseStatus, formatWarehouseTimestamp, formatWarehouseValue } from "./warehouse-api";

// Real per-category trait list — GET /traits/warehouse/catalog?category=&search=&page=&per_page=
// (confirmed live) — replaces the old "coming soon" placeholder that used to
// sit at /unify/traits/custom|computed|predictive (see main-routes.tsx).
// Mirrors the styling of the "Live Data" table on the main All Traits page
// (all-traits/index.tsx) but scoped server-side to one category, with the
// search/pagination that endpoint actually supports.

const friendlyDataType = (v: string) => v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PER_PAGE = 20;

const RowSkeleton: React.FC = () => (
  <tr className="border-b border-gray-50 dark:border-gray-800 last:border-0">
    <td colSpan={6} className="px-5 py-5">
      <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
    </td>
  </tr>
);

const CategoryTraitsPage: React.FC<{ icon: LucideIcon; title: string; category: "custom" | "computed" | "predictive" }> = ({
  icon: Icon,
  title,
  category,
}) => {
  const { data: warehouseStatus, loading: statusLoading } = useWarehouseStatus();
  const warehouseAvailable = !!(warehouseStatus?.enabled && warehouseStatus?.configured);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: catalog, loading, error } = useWarehouseCatalog(warehouseAvailable, {
    category,
    search: search.trim() || undefined,
    page,
    per_page: PER_PAGE,
  });

  const rows = catalog?.data || [];
  const total = catalog?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-1">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/15 shrink-0">
          <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        </span>
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Traits in this category and how many profiles carry each one</p>
        </div>
      </div>

      {!statusLoading && !warehouseAvailable ? (
        <div className="mt-8 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 px-5 py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect a data warehouse (Settings) to see real {title.toLowerCase()} here.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 mb-4 relative max-w-xs">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search traits"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
            {error ? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-base">
                  <thead>
                    <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                      {["Trait Name", "Data Type", "Example Value", "Profiles", "Coverage", "Last Computed"].map((h) => (
                        <th key={h} className="px-5 py-4 font-normal">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">
                          {search.trim() ? "No traits match your search." : "No trait definitions in this category yet."}
                        </td>
                      </tr>
                    ) : (
                      rows.map((t) => (
                        <tr key={t.trait_name} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                          <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[220px] truncate" title={t.trait_name}>
                            {t.trait_name}
                          </td>
                          <td className="px-5 py-5 text-gray-900 dark:text-gray-100">{friendlyDataType(t.data_type || "—")}</td>
                          <td className="px-5 py-5 text-gray-900 dark:text-gray-100 max-w-[220px] truncate" title={formatWarehouseValue(t.example_value)}>
                            {formatWarehouseValue(t.example_value)}
                          </td>
                          <td className="px-5 py-5 text-gray-900 dark:text-gray-100">{t.profile_count.toLocaleString()}</td>
                          <td className="px-5 py-5">
                            <div className="w-14">
                              <p className="text-sm text-gray-900 dark:text-gray-100">{t.coverage.toFixed(1)}%</p>
                              <div className="h-1 rounded-full bg-indigo-100 dark:bg-gray-800 overflow-hidden mt-1">
                                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(t.coverage / 4, 4)}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatWarehouseTimestamp(t.last_computed_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryTraitsPage;
